/* ═══════════════════════════════════════════════════════════════════
   Synchronisation multi-écrans — port fidèle de `prototype-cta/sync.js`.

   Modèle : UN écran MAÎTRE porte l'état de référence et le diffuse ; les
   autres écrans sont des RÉPLIQUES qui affichent l'état reçu et renvoient
   des COMMANDES au maître. Si le maître se ferme (ou passe en arrière-plan),
   une réplique visible reprend la main automatiquement.

   Transport : BroadcastChannel (même origine), repli localStorage pour les
   messages de contrôle (l'état complet ne transite JAMAIS par localStorage).

   NB : dans l'app officielle, le serveur reste la source de vérité via
   Socket.IO. Cette couche reproduit le comportement multi-écrans du
   prototype par-dessus ; le maître diffuse l'état qu'il tient du serveur.
   ═══════════════════════════════════════════════════════════════════ */

export type SyncRole = 'master' | 'satellite';

export interface SyncPeer {
  ts: number;
  screen: string;
  role: SyncRole;
}

interface SyncMsg {
  type: 'state' | 'cmd' | 'hello' | 'bye';
  from?: string;
  payload?: unknown;
  name?: string;
  args?: unknown;
  screen?: string;
  role?: SyncRole;
  _n?: number;
}

interface SyncState {
  id: string;
  role: SyncRole;
  channel: BroadcastChannel | null;
  available: boolean;
  peers: Map<string, SyncPeer>;
  screen: string;
  onState: ((payload: unknown) => void) | null;
  onCmd: ((name: string, args: unknown) => void) | null;
  lastStateAt: number;
}

export const SYNC: SyncState = {
  id: Math.random().toString(36).slice(2, 9).toUpperCase(),
  role: 'satellite',
  channel: null,
  available: false,
  peers: new Map(),
  screen: 'full',
  onState: null,
  onCmd: null,
  lastStateAt: 0,
};

const MASTER_KEY = 'opsecours.cta.master';
const LS_BUS_KEY = 'opsecours.cta.bus';
const MASTER_TTL = 2500; // ms sans battement → maître réputé mort

function syncPost(msg: SyncMsg): void {
  msg.from = SYNC.id;
  if (SYNC.channel) {
    SYNC.channel.postMessage(msg);
    return;
  }
  // Repli localStorage : uniquement les messages de contrôle, jamais l'état.
  if (msg.type === 'state') return;
  try {
    localStorage.setItem(LS_BUS_KEY, JSON.stringify({ ...msg, _n: Math.random() }));
  } catch {
    /* quota */
  }
}

function syncHandle(msg: SyncMsg | null): void {
  if (!msg || msg.from === SYNC.id) return;
  switch (msg.type) {
    case 'state':
      if (SYNC.role === 'satellite' && SYNC.onState) {
        SYNC.onState(msg.payload);
        SYNC.lastStateAt = Date.now();
      }
      break;
    case 'cmd':
      if (SYNC.role === 'master' && SYNC.onCmd) SYNC.onCmd(msg.name || '', msg.args);
      break;
    case 'hello':
      SYNC.peers.set(msg.from as string, {
        ts: Date.now(),
        screen: msg.screen || 'full',
        role: msg.role || 'satellite',
      });
      break;
    case 'bye':
      SYNC.peers.delete(msg.from as string);
      break;
  }
}

/* ── Élection du maître ──
   Le navigateur gèle les minuteurs des fenêtres masquées : on laisse donc une
   fenêtre VISIBLE préempter un maître masqué pour ne pas figer la diffusion. */
function claimMasterIfFree(): boolean {
  let m: { id: string; ts: number; hidden: boolean } | null = null;
  try {
    m = JSON.parse(localStorage.getItem(MASTER_KEY) || 'null');
  } catch {
    /* corrompu */
  }
  const now = Date.now();
  const moiVisible = !document.hidden;

  const libre =
    !m ||
    m.id === SYNC.id ||
    now - m.ts > MASTER_TTL ||
    (m.hidden && moiVisible);

  if (libre) {
    try {
      localStorage.setItem(MASTER_KEY, JSON.stringify({ id: SYNC.id, ts: now, hidden: document.hidden }));
    } catch {
      /* ignore */
    }
    const promu = SYNC.role !== 'master';
    SYNC.role = 'master';
    return promu;
  }
  SYNC.role = 'satellite';
  return false;
}

function syncHeartbeat(onPromotion?: () => void): void {
  const promu = claimMasterIfFree();
  syncPost({ type: 'hello', screen: SYNC.screen, role: SYNC.role });
  const now = Date.now();
  SYNC.peers.forEach((p, id) => {
    if (now - p.ts > 3500) SYNC.peers.delete(id);
  });
  if (promu && onPromotion) onPromotion();
}

interface SyncInitOpts {
  screen: string;
  onState: (payload: unknown) => void;
  onCmd: (name: string, args: unknown) => void;
  onPromotion?: () => void;
  onTick?: () => void; // appelé à chaque battement (pour rafraîchir l'UI React)
}

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let storageListener: ((e: StorageEvent) => void) | null = null;
let visibilityListener: (() => void) | null = null;
let unloadListener: (() => void) | null = null;

export function syncInit({ screen, onState, onCmd, onPromotion, onTick }: SyncInitOpts): void {
  SYNC.screen = screen;
  SYNC.onState = onState;
  SYNC.onCmd = onCmd;

  try {
    SYNC.channel = new BroadcastChannel('opsecours-cta');
    SYNC.channel.onmessage = (e) => syncHandle(e.data as SyncMsg);
    SYNC.available = true;
  } catch {
    SYNC.channel = null;
  }

  storageListener = (e: StorageEvent) => {
    if (e.key !== LS_BUS_KEY || !e.newValue) return;
    try {
      syncHandle(JSON.parse(e.newValue));
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('storage', storageListener);
  try {
    localStorage.setItem('opsecours.cta.probe', '1');
    SYNC.available = true;
  } catch {
    SYNC.available = false;
  }

  claimMasterIfFree();
  syncHeartbeat(onPromotion);
  heartbeatTimer = setInterval(() => {
    syncHeartbeat(onPromotion);
    if (onTick) onTick();
  }, 1000);

  visibilityListener = () => {
    if (!document.hidden) syncHeartbeat(onPromotion);
  };
  document.addEventListener('visibilitychange', visibilityListener);

  unloadListener = () => {
    syncPost({ type: 'bye' });
    try {
      const m = JSON.parse(localStorage.getItem(MASTER_KEY) || 'null');
      if (m && m.id === SYNC.id) localStorage.removeItem(MASTER_KEY);
      if (SYNC.peers.size === 0) localStorage.removeItem(LS_BUS_KEY);
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('beforeunload', unloadListener);
}

/** Démontage propre (utile en dev/StrictMode et au changement de page). */
export function syncTeardown(): void {
  if (unloadListener) unloadListener();
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  if (storageListener) window.removeEventListener('storage', storageListener);
  if (visibilityListener) document.removeEventListener('visibilitychange', visibilityListener);
  if (SYNC.channel) {
    try {
      SYNC.channel.close();
    } catch {
      /* ignore */
    }
  }
  SYNC.channel = null;
  SYNC.peers.clear();
  heartbeatTimer = null;
  storageListener = null;
  visibilityListener = null;
  unloadListener = null;
}

/** Maître → répliques : diffusion de l'état autoritaire. */
export function syncBroadcastState(payload: unknown): void {
  if (SYNC.role !== 'master') return;
  syncPost({ type: 'state', payload });
}

/** Réplique → maître : demande d'action. */
export function syncSendCmd(name: string, args?: unknown): void {
  syncPost({ type: 'cmd', name, args });
}

/** Nombre d'écrans connectés, celui-ci compris. */
export const syncScreenCount = (): number => SYNC.peers.size + 1;
